import { memo } from 'react'
import { toast } from 'react-toastify'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import {
  CheckCircle,
  FileTextIcon,
  ImageIcon,
  PdfIcon,
  WarningIcon,
} from '../../../../components/Icons'
import {
  useRemoveKnowledgeFile,
  // useRemoveKnowledgeFile,
  useRemoveUploadedFile,
} from '../../../../hooks/apiHooks/twinHooks'
import { useTwinData } from '../../../../hooks/storeHooks/useTwin'
import { IKnowledgeFile } from '../../../../store/reducers/twinReducer'

interface IKnowledgeFileItemProps {
  storedFile: IKnowledgeFile
  retryUpload: (file: File, retry: boolean) => void
  mentorId: string
  isReadyStatus: boolean
  orgId: string
  clearInput: () => void
}

const getFileIcon = (fileName: string): React.ReactNode => {
  const extension = fileName.split('.').pop()?.toLowerCase()
  switch (extension) {
    case 'pdf':
      return <PdfIcon size={24} />
    case 'doc':
    case 'docx':
    case 'txt':
    case 'md':
      return <FileTextIcon />
    case 'png':
    case 'jpeg':
    case 'jpg':
      return <ImageIcon size={24} />
    default:
      return <FileTextIcon />
  }
}

const KnowledgeFileItem: React.FC<IKnowledgeFileItemProps> = memo(
  ({ retryUpload, storedFile, mentorId, isReadyStatus, orgId, clearInput }) => {
    const { mutateAsync: removeUploadedFile } = useRemoveUploadedFile()
    const { mutateAsync: removeKnowledgeFileAPI } = useRemoveKnowledgeFile()
    const { removeKnowledgeFile, updateKnowledgeFileStatus } = useTwinData()
    const reUpload = () => {
      if (!storedFile.file) return
      retryUpload(storedFile.file, true)
    }

    const renderStatus = () => {
      switch (storedFile.status) {
        case 'uploading':
          return <AnimatedSpinner size={16} />
        case 'uploaded':
          return (
            <div className="w-2 h-2 rounded-full flex items-center justify-center bg-white overflow-visible">
              <div className="text-green-200">
                <CheckCircle size={16} />
              </div>
            </div>
          )
        case 'error':
          return (
            <button
              onClick={reUpload}
              className="text-red-500 flex items-center gap-1"
            >
              Retry
              <WarningIcon width={16} />
            </button>
          )
        default:
          return null
      }
    }

    const handleDelete = () => {
      if (storedFile.status === 'uploaded') {
        updateKnowledgeFileStatus({
          ...storedFile,
          status: 'uploading',
        })
        if (isReadyStatus) {
          removeKnowledgeFileAPI({
            uploadId: storedFile.id,
            mentorId,
          })
            .then(() => {
              removeKnowledgeFile(storedFile.fileName)
              toast.success('File deleted successfully!')
            })
            .catch(() => {
              updateKnowledgeFileStatus({
                ...storedFile,
                status: 'uploaded',
              })
              toast.error('Failed to delete file')
            })
            .finally(() => {
              clearInput()
            })
          return
        }

        removeUploadedFile({
          id: storedFile.id,
          organization_id: orgId,
        })
          .then(() => {
            removeKnowledgeFile(storedFile.fileName)
          })
          .catch(() => {
            updateKnowledgeFileStatus({
              ...storedFile,
              status: 'uploaded',
            })
            toast.error('Failed to delete file')
          })
          .finally(() => {
            clearInput()
          })
      }
    }

    return (
      <div className="tc-mv-blur flex gap-2 px-4 py-3 w-full items-center">
        {getFileIcon(storedFile.fileName)}
        <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
          {storedFile.fileName}
        </div>
        <div className="flex items-center justify-center ms-auto">
          {renderStatus()}
        </div>
        <button
          onClick={handleDelete}
          className="ms-2"
          disabled={storedFile.status === 'uploading'}
        >
          <i className="fa-solid fa-trash" />
        </button>
        <div className="hidden">
          <CheckCircle />
        </div>
      </div>
    )
  },
)

export { KnowledgeFileItem }
